import {
  AppShell,
  Button,
  Group,
  Loader,
  Skeleton,
  Table,
  Text,
} from "@mantine/core";
import { useGetVentsForUserAsGuestQuery } from "../slices/api/ventsApi";
import { JSX, useState } from "react";
import dayjs from "dayjs";
import { useLocation } from "wouter";
import { TextSearchBox } from "../shared/components/TextSearchBox";
import {
  IconGuests,
  IconRsvp,
  IconActivities,
} from "../shared/components/Icons";
import { handleFetchingError } from "../shared/ErrorMessage";
import { EbTooltip } from "../shared/components/EbTooltip";
import { LinkAnchorToVentHome } from "../shared/components/LinkAnchors";
import { getLinkToVentGuests, getLinkToVentActivities } from "../shared/links";

function VentsGuestTab() {
  const [_, navigate] = useLocation();
  const [searchText, setSearchText] = useState("");

  const {
    data: ventResponse,
    isFetching: isFetchingVents,
    isError: isErrorFetchingVents,
    error: ventsError,
  } = useGetVentsForUserAsGuestQuery({});

  if (isErrorFetchingVents) {
    handleFetchingError(ventsError);
    return null;
  }

  if (isFetchingVents || !ventResponse) {
    return (
      <AppShell.Main>
        <Loader color="blue" />
      </AppShell.Main>
    );
  }

  let rows: JSX.Element[] = [];
  if (ventResponse.vents?.length > 0) {
    const filteredVents = ventResponse.vents.filter((vent) =>
      vent.name.toLowerCase().includes(searchText.toLowerCase())
    );
    const sorted = filteredVents.sort((a, b) => (a.starts < b.starts ? 1 : -1));
    rows = sorted.map((vent) => (
      <Table.Tr key={vent.id}>
        <Table.Td>
          <Group gap="3px">
            <EbTooltip label="Go to RSVP page">
              <Button
                mt="0"
                mb="0"
                size="compact-xs"
                onClick={() =>
                  navigate(
                    `/console/guest/event/${vent.id}/guest/${vent.userGuestId}`
                  )
                }
                rightSection={<IconRsvp />}
                styles={{
                  root: { width: "70px" },
                  section: {
                    marginLeft: 2, // Adjust as needed to pull closer
                  },
                }}
              >
                RSVP
              </Button>
            </EbTooltip>
            {vent.canUserViewGuests && (
              <EbTooltip label="View guests">
                <Button
                  mt="0"
                  mb="0"
                  size="compact-xs"
                  onClick={() => navigate(getLinkToVentGuests(vent.id))}
                  rightSection={<IconGuests />}
                  styles={{
                    root: { width: "70px" },
                    section: {
                      marginLeft: 2,
                    },
                  }}
                >
                  Guests
                </Button>
              </EbTooltip>
            )}
            {vent.ventHasActivities && (
              <EbTooltip label="View activities">
                <Button
                  mt="0"
                  mb="0"
                  size="compact-xs"
                  onClick={() => navigate(getLinkToVentActivities(vent.id))}
                  rightSection={<IconActivities />}
                  styles={{
                    root: { width: "70px" },
                    section: {
                      marginLeft: 2,
                    },
                  }}
                >
                  Activities
                </Button>
              </EbTooltip>
            )}
          </Group>
        </Table.Td>
        <Table.Td>
          <LinkAnchorToVentHome ventId={vent.id}>
            {vent.name}
          </LinkAnchorToVentHome>
        </Table.Td>
        <Table.Td>{dayjs(vent.starts).format("LLL")}</Table.Td>
        <Table.Td>{dayjs(vent.ends).format("LLL")}</Table.Td>
      </Table.Tr>
    ));
  }
  return (
    <AppShell.Main>
      <Text mb="0" size="xl">
        Events as Guest
      </Text>
      <TextSearchBox setSearchText={setSearchText} searchText={searchText} />
      <Skeleton visible={isFetchingVents}>
        <div hidden={rows.length === 0}>
          <Table stickyHeader stickyHeaderOffset={0}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Actions</Table.Th>
                <Table.Th>Event</Table.Th>
                <Table.Th>Starts</Table.Th>
                <Table.Th>Ends</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>
        </div>
        <div hidden={isFetchingVents || rows.length > 0}>
          No events displayed. Change search or add an event.
        </div>
      </Skeleton>
    </AppShell.Main>
  );
}

export default VentsGuestTab;
